import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { BookOpen } from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Blog posts data
  const blogPosts = [
    {
      id: 1,
      slug: 'why-stick-audio-is-5x-cheaper-than-eleven-labs',
      title: 'Why Stick Audio is 5x Cheaper Than Eleven Labs',
      date: 'February 4, 2026',
      excerpt: 'Discover how Stick Audio offers enterprise-grade quality at a fraction of the cost of Eleven Labs.',
      content: (
        <>
          <p>
            Text-to-speech technology has been revolutionizing how we create audio content, but most platforms come with hefty price tags.
            Eleven Labs, while popular, charges significantly for each character generated, making it expensive for developers and businesses.
          </p>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>How Stick Audio Saves You Money</h3>
          <p>
            At Stick Audio, we believe quality audio generation shouldn't break the bank. Here's how we offer 5x better pricing:
          </p>

          <ul style={{ paddingLeft: '20px' }}>
            <li><strong>Transparent Pricing:</strong> Pay only 1 credit per character. No hidden fees or surprise charges.</li>
            <li><strong>Free Tier:</strong> Start with 2,000 free characters - enough to test and prototype.</li>
            <li><strong>Bulk Discounts:</strong> Buy more credits and save more. Our Creator plan gives you 400,000 characters for just $20.</li>
            <li><strong>Lifetime Credits:</strong> Your credits never expire, unlike other platforms with monthly quotas.</li>
            <li><strong>No Subscription Trap:</strong> Pay only for what you use. Cancel anytime, no commitments.</li>
          </ul>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>Real Cost Comparison</h3>
          <p>Let's compare generating 1 million characters:</p>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px', marginBottom: '15px' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(255, 215, 0, 0.1)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--primary-color)', borderBottom: '2px solid rgba(255, 215, 0, 0.2)' }}>Platform</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--primary-color)', borderBottom: '2px solid rgba(255, 215, 0, 0.2)' }}>Cost for 1M Characters</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'var(--primary-color)', borderBottom: '2px solid rgba(255, 215, 0, 0.2)' }}>Price Per Character</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--text-secondary)' }}>Eleven Labs</td>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--text-secondary)' }}>$100+</td>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--text-secondary)' }}>$0.0001/char</td>
              </tr>
              <tr style={{ backgroundColor: 'rgba(255, 215, 0, 0.08)' }}>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--primary-color)', fontWeight: '600' }}>Stick Audio</td>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--primary-color)', fontWeight: '600' }}>$20 (Pro plan)</td>
                <td style={{ padding: '12px', borderBottom: '1px solid rgba(255, 215, 0, 0.1)', color: 'var(--primary-color)', fontWeight: '600' }}>$0.00002/char</td>
              </tr>
            </tbody>
          </table>

          <p>
            <strong>That's up to 5x cheaper!</strong> With Stick Audio, you get enterprise-grade quality at a fraction of the cost.
          </p>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>The Quality Difference? There Isn't One</h3>
          <p>
            Stick Audio uses the same advanced Resemble AI Chatterbox Turbo model that powers industry-leading applications.
            You get the same natural-sounding voices and quality audio at a significantly lower price.
          </p>

          <p>
            Lower costs don't mean lower quality. We're just more efficient and pass those savings to you.
          </p>
        </>
      ),
    },
    {
      id: 2,
      slug: 'unlimited-voice-cloning-at-no-extra-cost',
      title: 'Unlimited Voice Cloning At No Extra Cost',
      date: 'February 3, 2026',
      excerpt: 'Create custom voices without setup fees or per-use charges. Clone unlimited voices and experiment freely.',
      content: (
        <>
          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>What is Voice Cloning?</h3>
          <p>
            Voice cloning technology allows you to create custom voices by uploading voice samples.
            Once trained, these custom voices can be used to generate unlimited audio, giving your content a unique, branded sound.
          </p>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>The Usual Cost Problem</h3>
          <p>Most platforms charge extra for voice cloning:</p>

          <ul style={{ paddingLeft: '20px' }}>
            <li>Setup fees: $50-$200 just to create a custom voice</li>
            <li>Per-use charges: Additional cost every time you use the custom voice</li>
            <li>Monthly fees: Subscription costs on top of character costs</li>
          </ul>

          <p>
            This makes it expensive to maintain multiple branded voices or experiment with different voice profiles.
          </p>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>Stick Audio's Approach: Unlimited Voice Cloning</h3>
          <p>At Stick Audio, voice cloning is completely free. Here's what you get:</p>

          <ul style={{ paddingLeft: '20px' }}>
            <li><strong>No Setup Fee:</strong> Create as many custom voices as you want for free</li>
            <li><strong>No Extra Charges:</strong> Use custom voices just like built-in voices - 1 credit per character</li>
            <li><strong>Unlimited Options:</strong> Clone unlimited voices and experiment freely</li>
            <li><strong>Perfect for Branding:</strong> Give your entire audio content a consistent, branded voice</li>
          </ul>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>Use Cases for Voice Cloning</h3>
          <p>Unlimited voice cloning opens up new possibilities:</p>

          <ul style={{ paddingLeft: '20px' }}>
            <li><strong>Brand Consistency:</strong> Use the same voice across all your content</li>
            <li><strong>Personal Assistants:</strong> Create a signature voice for your AI assistant</li>
            <li><strong>Multiple Characters:</strong> Generate different voices for dialogues and character interactions</li>
            <li><strong>Language Variations:</strong> Clone voices in different languages and accents</li>
            <li><strong>A/B Testing:</strong> Test different voices without worrying about extra costs</li>
          </ul>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>How It Works</h3>
          <p>Using Stick Audio's voice cloning is incredibly simple:</p>

          <ol style={{ paddingLeft: '20px' }}>
            <li>Record or upload a voice sample (10-60 seconds in MP3 or WAV)</li>
            <li>Give your custom voice a name</li>
            <li>Start using it immediately in text-to-speech generation</li>
            <li>Clone more voices anytime - no limits, no extra cost</li>
          </ol>

          <h3 style={{ marginTop: '20px', fontWeight: '600', color: 'var(--primary-color)' }}>Why This Matters</h3>
          <p>
            Voice cloning shouldn't be a premium feature reserved for big-budget projects.
            By offering unlimited voice cloning at no extra cost, Stick Audio democratizes personalized audio generation
            for everyone from indie developers to enterprise teams.
          </p>

          <p>
            Create your perfect voice today and generate unlimited audio with it.
          </p>
        </>
      ),
    },
  ];

  // Find current blog post
  const currentPost = slug ? blogPosts.find(post => post.slug === slug) : null;
  const currentPostIndex = slug ? blogPosts.findIndex(post => post.slug === slug) : -1;

  // SEO configuration based on current view
  const getSEOConfig = () => {
    if (currentPost) {
      return {
        title: `${currentPost.title} - Stick Audio Blog`,
        description: currentPost.excerpt,
        keywords: currentPost.id === 1 ? 'text-to-speech, Eleven Labs alternative, voice cloning, cheaper TTS' : 'voice cloning, unlimited voices, TTS technology',
      };
    }
    return {
      title: 'Blog - Stick Audio Text-to-Speech',
      description: 'Discover how Stick Audio is 5x cheaper than Eleven Labs and offers unlimited voice cloning at no extra cost. Learn about AI voice generation and TTS technology.',
      keywords: 'text-to-speech, Eleven Labs alternative, voice cloning, cheaper TTS, AI voices',
    };
  };

  useSEO(getSEOConfig());

  return (
    <div className="policy-page-container">
      {/* Header */}
      <header className="policy-page-header">
        <div className="policy-header-content">
          <h1>Blog</h1>
          <button className="btn-home" onClick={() => navigate('/')}>
            🏠 HOME
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="content-wrapper">
        <div className="content-inner">
          <div className="card-container">
            {/* Back button and Header */}
            {currentPost ? (
              <div style={{ marginBottom: '32px' }}>
                <button
                  onClick={() => navigate('/blogs')}
                  style={{
                    background: 'rgba(255, 215, 0, 0.1)',
                    border: '1px solid var(--primary-color)',
                    color: 'var(--primary-color)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '16px',
                    fontWeight: '500',
                  }}
                >
                  ← Back to Blog
                </button>

                <h1 style={{ margin: '16px 0 8px 0', color: 'var(--text-primary)' }}>{currentPost.title}</h1>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0' }}>Published: {currentPost.date}</p>
              </div>
            ) : (
              <div style={{ marginBottom: '32px' }}>
                <h2 className="section-title mb-2">
                  <BookOpen size={20} strokeWidth={2} style={{ marginRight: '8px', display: 'inline-block' }} />
                  Blog
                </h2>
                <p className="section-subtitle mb-4">Insights on AI-powered text-to-speech technology</p>
              </div>
            )}

          <div style={{ display: 'grid', gridTemplateColumns: currentPost ? '1fr' : '1fr 280px', gap: '32px' }}>
            {/* Main Content */}
            <div>
              {currentPost ? (
                <div className="blog-post-content">
                  {currentPost.content}

                  {/* Navigation */}
                  <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between' }}>
                    {currentPostIndex > 0 ? (
                      <button
                        onClick={() => navigate(`/blogs/${blogPosts[currentPostIndex - 1].slug}`)}
                        style={{
                          background: 'rgba(255, 215, 0, 0.1)',
                          border: '1px solid var(--primary-color)',
                          color: 'var(--primary-color)',
                          padding: '10px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        ← Previous
                      </button>
                    ) : <div />}

                    {currentPostIndex < blogPosts.length - 1 ? (
                      <button
                        onClick={() => navigate(`/blogs/${blogPosts[currentPostIndex + 1].slug}`)}
                        style={{
                          background: 'rgba(255, 215, 0, 0.1)',
                          border: '1px solid var(--primary-color)',
                          color: 'var(--primary-color)',
                          padding: '10px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        Next →
                      </button>
                    ) : <div />}
                  </div>
                </div>
              ) : (
                <div className="blog-posts-list">
                  {blogPosts.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => navigate(`/blogs/${post.slug}`)}
                      style={{
                        cursor: 'pointer',
                        padding: '24px',
                        background: 'rgba(22, 23, 29, 0.4)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(22, 23, 29, 0.6)';
                        e.currentTarget.style.borderColor = 'var(--primary-color)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(22, 23, 29, 0.4)';
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                      }}
                    >
                      <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>{post.title}</h3>
                      <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>Published: {post.date}</p>
                      <p style={{ margin: '0', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{post.excerpt}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - Only show on blog list view and not on mobile */}
            {!currentPost && (
              <div style={{ display: 'grid', gridAutoRows: 'max-content' }}>
                <div
                  style={{
                    background: 'rgba(22, 23, 29, 0.6)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: 'var(--primary-color)' }}>
                    All Posts
                  </h3>
                  <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
                    {blogPosts.map((post) => (
                      <li key={post.id} style={{ marginBottom: '12px' }}>
                        <a
                          href={`/blogs/${post.slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/blogs/${post.slug}`);
                          }}
                          style={{
                            display: 'block',
                            color: 'var(--primary-color)',
                            textDecoration: 'none',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            padding: '8px',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {post.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Blog;
